import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { WifiOff, Wifi, X } from "lucide-react";

/**
 * Standalone WhatsApp disconnected banner.
 * Fetches WA status independently — works inside or outside MainLayout.
 * showAlways=true: mostra sempre que desconectado, sem verificar chatbot_enabled (para /orders)
 */
export function WABanner({ showAlways = false }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [connected, setConnected] = useState(true);
  const [chatbotEnabled, setChatbotEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const checkStatus = useCallback(async () => {
    try {
      const [statusRes, restaurantRes] = await Promise.allSettled([
        authAxiosRef.current.get("/chatbot/whatsapp/status"),
        authAxiosRef.current.get("/restaurant"),
      ]);
      let enabled = false;
      if (restaurantRes.status === "fulfilled") {
        enabled = restaurantRes.value.data?.chatbot_enabled === true;
        setChatbotEnabled(enabled);
      }
      if (statusRes.status === "fulfilled") {
        const isConnected = statusRes.value.data?.status === "connected";
        setConnected(showAlways ? isConnected : (isConnected || !enabled));
      }
    } catch {
      // keep current state
    }
  }, []);

  useEffect(() => {
    checkStatus();
    const id = setInterval(checkStatus, 30_000);
    return () => clearInterval(id);
  }, [checkStatus]);

  // Reset dismissed when connection drops again
  useEffect(() => {
    if (!connected && chatbotEnabled) setDismissed(false);
  }, [connected, chatbotEnabled]);

  if (dismissed || connected || (!chatbotEnabled && !showAlways)) return null;

  return (
    <div
      className="bg-red-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 shrink-0"
      data-testid="wa-banner"
    >
      <div className="flex items-center gap-3">
        <WifiOff className="w-5 h-5 flex-shrink-0" />
        <p className="text-sm font-medium">
          {showAlways ? (
            <>
              <span className="font-bold">WhatsApp desconectado</span>
              {" — o chatbot não está respondendo e mensagens aos clientes não serão entregues."}
            </>
          ) : (
            <>
              <span className="font-bold">{t("admin.whatsappDisconnected")}</span>{" "}
              {t("admin.whatsappDisconnectedMsg")}
            </>
          )}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          onClick={() => { setLoading(true); navigate("/chatbot"); }}
          className="px-3 py-1 bg-white/20 rounded text-sm font-bold hover:bg-white/30 transition-colors flex items-center gap-1.5"
          data-testid="wa-banner-reconnect"
          disabled={loading}
        >
          <Wifi className="w-3.5 h-3.5" />
          {loading ? t("common.loading") : t("admin.reconnect")}
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 hover:bg-white/20 rounded"
          data-testid="wa-banner-dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
